import React from "react";

import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertConfig, TimeBelowRangeCGM } from "@/models/AlertModel.ts";
import { HappenedInTheLastXDays } from "@/pages/patient-settings/alert-settings/inputs/HappenedInTheLastXDays.tsx";
import { NumberOfOccurrences } from "@/pages/patient-settings/alert-settings/inputs/NumberOfOccurrences.tsx";
import { SelectType } from "@/pages/patient-settings/alert-settings/inputs/SelectType.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

export type AlertHypoglycemiaTbrSettingsProps = {
  patientId: string;
  alertConfig: AlertConfig;
  parameters: TimeBelowRangeCGM;
};

export const AlertHypoglycemiaTbrSettings: React.FC<AlertHypoglycemiaTbrSettingsProps> = ({
  parameters,
  patientId,
  alertConfig,
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const editAlertConfig = Queries.alerts.useEditAlertConfig();
  const updateParameters = (value: unknown, key: keyof TimeBelowRangeCGM) => {
    editAlertConfig.mutate(
      {
        patientId,
        alertConfig: {
          id: alertConfig.id,
          parameters: { ...parameters, [key]: value },
        },
      },
      {
        onSuccess: () =>
          TextToast.success(
            t("alertConfig.conditionsSuccess", {
              alert: t(alertCategoriesTranslations.time_below_range),
            }),
          ),
        onError: () =>
          TextToast.error(
            t("alertConfig.conditionsError", {
              alert: t(alertCategoriesTranslations.time_above_range),
            }),
          ),
      },
    );
  };

  return (
    <div className={styles.container}>
      <NumberOfOccurrences
        id={`alert-config-${alertConfig.id}-occurrences`}
        textBefore={t("alertConfig.moreThan")}
        textAfter={`%`}
        onCountChange={count => updateParameters(count / 100.0, "durationProportion")}
        minCount={0}
        maxCount={100}
        count={formatNumber(parameters.durationProportion * 100, 0)}
      />
      <Typography variant="body">{t("alertConfig.timeSpentInHypo")}</Typography>
      <SelectType
        id={`alert-config-${alertConfig.id}-severity`}
        onSelectChange={type => updateParameters(type, "severeOnly")}
        options={[
          { value: false, text: t("alertConfig.lowOrVeryLow") },
          { value: true, text: t("alertConfig.veryLow") },
        ]}
        selected={parameters.severeOnly}
      />
      <HappenedInTheLastXDays
        id={`alert-config-${alertConfig.id}-days`}
        onDaysChange={days => updateParameters(days, "numberOfDays")}
        minDays={1}
        maxDays={365}
        days={parameters.numberOfDays}
      />
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
});
